import { getFacilitiesPerformanceQueryOptions } from '@/entities/facility';
import { SortDirection } from '@/shared/api/lib';
import { PerformanceSortField } from '@/shared/api/modules/facilities';
import { Icon } from '@/shared/assets/icons';
import { Loader, TooltipElement, Typography } from '@/shared/ui';
import { useQuery } from '@tanstack/react-query';
import type { Dayjs } from 'dayjs';
import type { FC } from 'react';

type FacilityPerformanceKpiCardProps = {
  facilityId: number;
  dateRange: {
    from: Dayjs;
    to: Dayjs;
  };
};

export const FacilityPerformanceKpiCard: FC<
  FacilityPerformanceKpiCardProps
> = ({ facilityId, dateRange }) => {
  const { data, isLoading } = useQuery(
    getFacilitiesPerformanceQueryOptions({
      from: dateRange.from.format('YYYY-MM-DD'),
      to: dateRange.to.format('YYYY-MM-DD'),
      page: 1,
      perPage: 1000,
      sortBy: PerformanceSortField.PERFORMANCE_SCORE,
      sortDirection: SortDirection.DESC,
      // Benchmark this facility against every facility in the ownership group,
      // not just the ones the user manages.
      includeAllOrgFacilities: true,
    })
  );

  if (isLoading) {
    return (
      <div className="bg-neutral-1 border-neutral-4 flex h-full items-center justify-center border px-6 py-4">
        <Loader />
      </div>
    );
  }

  if (!data || data.data.length === 0) {
    return (
      <div className="bg-neutral-1 border-neutral-4 flex h-full items-center justify-center border px-6 py-4">
        <Typography className="text-neutral-6">No data available</Typography>
      </div>
    );
  }

  const sorted = [...data.data].sort(
    (a, b) => b.performanceScore - a.performanceScore
  );
  const total = sorted.length;
  const ownIndex = sorted.findIndex((f) => f.id === facilityId);

  if (ownIndex === -1) {
    return (
      <div className="bg-neutral-1 border-neutral-4 flex h-full items-center justify-center border px-6 py-4">
        <Typography className="text-neutral-6">No data available</Typography>
      </div>
    );
  }

  const rank = ownIndex + 1;
  const ownScore = sorted[ownIndex].performanceScore;
  const betterCount = sorted.filter(
    (f) => f.performanceScore > ownScore
  ).length;
  const worseCount = sorted.filter((f) => f.performanceScore < ownScore).length;
  const progressPct = Math.min(100, Math.max(0, ownScore));

  return (
    <div className="bg-neutral-1 border-neutral-4 flex h-full flex-col gap-2 border px-6 py-4">
      <Icon name="chart" className="text-neutral-7" size={16} />

      <div className="flex items-center gap-x-2">
        <Typography noWrap className="text-neutral-8">
          Facility performance
        </Typography>
        <TooltipElement
          content="Facility Performance Score combines five metrics: Occupancy, Revenue per Resident, Margin, Median Length of Stay, and Alerts Resolution Effectiveness.
                  Each metric is normalized across all facilities belonging to the same owner and contributes up to 20 points, resulting in a total score from 0 to 100"
        />
      </div>

      <Typography variant="h3" weight="medium" className="text-neutral-12">
        {ownScore.toFixed(0)}{' '}
        <span className="text-neutral-8 text-base font-normal">
          / 100 points
        </span>
      </Typography>

      <div className="bg-neutral-4 h-2 w-3/4 overflow-hidden rounded-full">
        <div
          className="bg-primary-5 h-full rounded-full"
          style={{ width: `${progressPct}%` }}
        />
      </div>

      <div className="flex items-center gap-2">
        <span className="border-primary-5 bg-primary-1 text-neutral-8 rounded border px-2 py-0.5 text-sm">
          #{rank} of {total}
        </span>
        <Typography className="text-neutral-8" variant="footnote-description">
          Rank among all facilities
        </Typography>
      </div>

      <div className="mt-auto flex flex-col gap-1">
        <div className="text-polar-green-7 flex items-center gap-1.5">
          <Icon name="arrow-up" size={16} />
          <Typography variant="body" weight="medium">
            Better than {worseCount}{' '}
            {worseCount === 1 ? 'facility' : 'facilities'}
          </Typography>
        </div>
        <div className="text-dust-red-5 flex items-center gap-1.5">
          <Icon name="arrow-down" size={16} />
          <Typography variant="body" weight="medium">
            Worse than {betterCount}{' '}
            {betterCount === 1 ? 'facility' : 'facilities'}
          </Typography>
        </div>
      </div>
    </div>
  );
};
